import QRCode from "qrcode";

window.atlas_clearBlazorCache = async function () {
    const cacheNames = await caches.keys();
    await Promise.all(cacheNames.map(name => caches.delete(name)));

    const registrations = await navigator.serviceWorker.getRegistrations();
    for (let registration of registrations) {
        await registration.unregister();
    }
};

window.atlas_clearLocalStorage = async function () {
    await window.localStorage.clear();
}

window.atlas_reload = async function () {
    window.location.reload();
};

/*
 * Atlas Tectonic: pop-out panel windows.
 *
 * Handles are kept here in JS rather than handed to .NET as an IJSObjectReference. Blazor cannot resolve
 * members on the WindowProxy that window.open returns - calls like location.assign and close fail with
 * "the value is not a function" - so the window is addressed by name from .NET instead.
 *
 * Opening and navigating are separate on purpose. Browsers only allow window.open while the click's user
 * activation is still valid, and the handoff token needs a server round trip first. So the window is
 * opened blank on the click and navigated once the token arrives.
 */
const atlasPanelWindows = new Map<string, Window>();

window.atlas_openPanelWindow = function (name: string, width: number, height: number): boolean {
    // Must stay synchronous - anything awaited before this would risk losing the user activation.
    const panelWindow = window.open("", name, `popup=yes,width=${width},height=${height}`);

    if (!panelWindow) {
        // Blocked by the browser.
        return false;
    }

    atlasPanelWindows.set(name, panelWindow);
    return true;
};

window.atlas_navigatePanelWindow = function (name: string, url: string): boolean {
    const panelWindow = atlasPanelWindows.get(name);

    if (!panelWindow || panelWindow.closed) {
        atlasPanelWindows.delete(name);
        return false;
    }

    panelWindow.location.assign(url);
    panelWindow.focus();
    return true;
};

/**
 * Draws a QR code for a panel url into a canvas.
 *
 * Atlas Tectonic: the url carries a 32-character handoff token, so reading it off the screen and typing it on a tablet
 * is not realistic - it has to be scannable. Returns false rather than throwing so the dialog can fall back to showing
 * the link on its own.
 */
window.atlas_renderQrCode = async function (canvasId: string, text: string): Promise<boolean> {
    const canvas = document.getElementById(canvasId);

    if (!(canvas instanceof HTMLCanvasElement)) {
        return false;
    }

    try {
        // Error correction level M: the default, and enough for a code read off a screen at arm's length.
        await QRCode.toCanvas(canvas, text, { width: 260, margin: 1 });
        return true;
    } catch {
        return false;
    }
};

/**
 * The tab title as the page was served with it, kept so a suffix can be replaced rather than stacked.
 */
let atlasBaseDocumentTitle: string | null = null;

/**
 * Appends something to the browser tab's title, or clears it again with an empty string.
 *
 * Atlas has no Blazor HeadOutlet, so <PageTitle> is not available - and a pop-out needs this, because several of them
 * plus the main window are otherwise indistinguishable in the tab bar and the taskbar. Idempotent: the original title is
 * remembered on the first call, so calling it again replaces the suffix instead of adding a second one.
 */
window.atlas_setDocumentTitleSuffix = function (suffix: string): void {
    atlasBaseDocumentTitle ??= document.title;

    document.title = suffix ? `${atlasBaseDocumentTitle} - ${suffix}` : atlasBaseDocumentTitle;
};

window.atlas_closePanelWindow = function (name: string): void {
    const panelWindow = atlasPanelWindows.get(name);
    atlasPanelWindows.delete(name);

    // close() is permitted here because we opened it ourselves.
    if (panelWindow && !panelWindow.closed) {
        panelWindow.close();
    }
};